import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Search, Globe, Menu, X } from 'lucide-react';
import { companyInfo, navigation } from '../data/mock';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const NavDropdown = ({ title, items, id }) => (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid={`header-${id}-menu-trigger`} className="flex items-center gap-1 text-gray-800 hover:text-[#E30022] transition-colors duration-200 font-medium text-sm">
        {title}
        <ChevronDown className="h-4 w-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent data-testid={`header-${id}-menu`} className="bg-white border border-gray-200 shadow-lg rounded-md min-w-[200px]">
        {items.map((item, index) => (
          <DropdownMenuItem key={index} asChild className="hover:bg-gray-50">
            <Link to={item.href} data-testid={`header-${id}-${index}-link`} className="w-full text-gray-700 hover:text-[#E30022] transition-colors">
              {item.label}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <header className="bg-white sticky top-0 z-50 shadow-sm">
      {/* Top bar */}
      <div className="bg-[#E30022] text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-end sm:justify-between items-center gap-4 text-sm">
          <span data-testid="header-tagline" className="hidden sm:block">Votre partenaire de confiance en recrutement</span>
          <div className="flex items-center gap-4">
            <Link to="/bureaux" data-testid="header-contact-link" className="hover:underline">Contactez-nous</Link>
            <div className="flex items-center gap-1">
              <Globe className="h-4 w-4" />
              <span data-testid="header-language">FR</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" data-testid="header-home-link" className="flex min-w-0 items-center gap-2 sm:gap-3">
            <img 
              src="/images/brand/logo-icon-1x.webp"
              srcSet="/images/brand/logo-icon-1x.webp 1x, /images/brand/logo-icon-2x.webp 2x, /images/brand/logo-icon-3x.webp 3x"
              width="56" height="56"
              alt={companyInfo.name} 
              className="h-10 sm:h-14 w-auto shrink-0"
              data-testid="header-logo-icon"
            />
            <img 
              src="/images/brand/logo-text-1x.webp"
              srcSet="/images/brand/logo-text-1x.webp 1x, /images/brand/logo-text-2x.webp 2x, /images/brand/logo-text-3x.webp 3x"
              width="159" height="56"
              alt="Recrutement Suisse"
              className="h-10 sm:h-14 w-auto min-w-0 object-contain"
              data-testid="header-logo-text"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center gap-8">
            <NavDropdown id="candidats" title={navigation.candidats.title} items={navigation.candidats.items} />
            <NavDropdown id="employeurs" title={navigation.employeurs.title} items={navigation.employeurs.items} />
            <NavDropdown id="services" title={navigation.services.title} items={navigation.services.items} />
            <NavDropdown id="about" title={navigation.about.title} items={navigation.about.items} />
          </div>

          {/* Right side */}
          <div className="hidden xl:flex items-center gap-4">
            <Link to="/emplois" data-testid="header-job-search-link" aria-label="Rechercher un emploi" className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <Search className="h-5 w-5 text-gray-600" />
            </Link>
            <Link
              to="/recrutement"
              data-testid="header-recrutement-link"
              className="bg-[#E30022] text-white px-6 py-2.5 rounded hover:bg-[#c7001d] transition-colors duration-200 font-medium text-sm"
            >
              Recrutez avec nous
            </Link>
          </div>

          {/* Mobile menu button */}
          <button 
            className="xl:hidden p-2 shrink-0"
            data-testid="header-mobile-menu-button"
            aria-label={mobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div id="mobile-navigation" data-testid="header-mobile-menu" className="xl:hidden py-4 border-t max-h-[calc(100dvh-120px)] overflow-y-auto">
            <div className="space-y-4">
              {Object.values(navigation).map((nav, index) => (
                <div key={index} className="space-y-2">
                  <p data-testid={`header-mobile-section-${index}`} className="font-semibold text-gray-800">{nav.title}</p>
                  {nav.items.map((item, idx) => (
                    <Link
                      key={idx} 
                      to={item.href}
                      data-testid={`header-mobile-${index}-${idx}-link`}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block pl-4 text-gray-600 hover:text-[#E30022] transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              ))}
              <Link
                to="/recrutement"
                data-testid="header-mobile-recrutement-link"
                onClick={() => setMobileMenuOpen(false)}
                className="block bg-[#E30022] text-white px-6 py-3 rounded text-center font-medium mt-4"
              >
                Recrutez avec nous
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
